import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Alert,
  Modal,
  Badge,
  InputGroup,
  FormControl,
  Spinner,
  Dropdown,
  Pagination,
  Form,
  Image,
  Tab,
  Nav,
  Accordion,
  ListGroup,
} from "react-bootstrap";
import {
  PersonPlus,
  Pencil,
  Trash,
  Eye,
  Envelope,
  Telephone,
  Key,
  Search,
  CheckCircle,
  XCircle,
  Send,
  Filter,
  ThreeDotsVertical,
  ArrowClockwise,
  PersonCheck,
  PersonX,
  Building,
  FileText,
  Bank,
  ShieldCheck,
  FileEarmarkImage,
  FileEarmarkPdf,
  CardImage,
  FileCheck,
  ClipboardCheck,
  Download,
  ZoomIn,
  CreditCard,
  Shield,
  House,
  PersonBadge,
} from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import { Baseurl } from "../../baseurl";
import { useSelector } from "react-redux";

const API_BASE_URL = "http://localhost:5000/api";

const EmployeeList = () => {
  const { loginData } = useSelector((store) => store.auth);
  const navigate = useNavigate();

  // State management
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalShow, setImageModalShow] = useState(false);

  // Filter and pagination states
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const itemsPerPage = 10;

  // Fetch employees with filters and pagination
  const fetchEmployees = async (page = 1) => {
    try {
      if (!loginData) return;

      setLoading(true);

      const agencyId =
        loginData.role === "admin"
          ? loginData._id
          : loginData.organisation?.id;

      if (!agencyId) return;

      const response = await axios.get(
        `${Baseurl}/api/fieldOfficer/all/${agencyId}`,
      );

      let employees = response.data.data || [];

      if (filterRole !== "all") {
        employees = employees.filter((emp) => emp.role === filterRole);
      }

      if (filterStatus !== "all") {
        employees = employees.filter((emp) => emp.empStatus === filterStatus);
      }

      setEmployees(employees);
      setTotalEmployees(employees.length);
    } catch (error) {
      showAlert("Error fetching employees", "danger");
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEmployees(1);
  }, [filterRole, filterStatus]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchEmployees(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => {
      setAlert({ show: false, message: "", variant: "success" });
    }, 5000);
  };

  // Open modal for actions
  const openModal = (type, employee = null) => {
    setModalType(type);
    setCurrentEmployee(employee);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCurrentEmployee(null);
    setActionLoading(false);
    setSelectedImage(null);
  };

  // Handle delete employee
  const handleDelete = async () => {
    try {
      setActionLoading(true);

      const url = `${Baseurl}/api/field-officers/delete`;

      const resp = await axios.delete(url, {
        data: {
          _id: currentEmployee._id,
        },
        headers: {
          "Content-Type": "application/json",
        },
      });

      showAlert("Employee deleted successfully!", "success");
      fetchEmployees(currentPage);
      closeModal();
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error deleting employee",
        "danger",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handle employee status update
  const handleUpdateStatus = async (employee, newStatus, remarks = "") => {
    try {
      await axios.patch(`${Baseurl}/api/fieldofficer/${employee._id}/status`, {
        _id: employee._id,
        empStatus: newStatus,
        remarks: remarks || `Status changed to ${newStatus}`,
      });
      showAlert(
        `Employee status updated to ${newStatus} successfully!`,
        "success",
      );
      fetchEmployees(currentPage);
      closeModal();
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error updating employee status",
        "danger",
      );
    }
  };

  // Send OTP to employee
  const handleSendOtp = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/field-officers/send-otp`, {
        mobileNo: currentEmployee.mobileNo,
      });
      showAlert("OTP sent successfully!", "success");
      closeModal();
    } catch (error) {
      showAlert(error.response?.data?.message || "Error sending OTP", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  // Reset password (send reset OTP)
  const handleResetPassword = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/field-officers/send-reset-otp`, {
        email: currentEmployee.email,
      });
      showAlert("Password reset OTP sent to employee email!", "success");
      closeModal();
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error sending reset OTP",
        "danger",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Navigate to edit page
  const handleEdit = (employee) => {
    navigate(`/employees/edit/${employee._id}`, { state: { employee } });
  };

  // Navigate to add employee page
  const handleAddEmployee = () => {
    navigate("/employees/add");
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "agent":
        return "danger";
      case "inspector":
        return "warning";
      case "yardOwner":
        return "info";
      default:
        return "secondary";
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending":
        return "warning";
      case "Rework":
        return "info";
      case "Rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  // View image in modal
  const handleViewImage = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalShow(true);
  };
  // Open document in new tab
  const handleViewInNewTab = (url, fileName) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      showAlert("Document URL not available", "warning");
    }
  };
  // Download document
  const handleDownload = (url, fileName) => {
    // const link = document.createElement("a");
    // link.href = url;
    // link.download = fileName || "document";
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      showAlert("Document URL not available", "warning");
    }
  };

  // Render document preview
  const renderDocumentPreview = (docUrl, docName) => {
    if (!docUrl) {
      return (
        <Badge bg="secondary" className="me-2">
          Not Uploaded
        </Badge>
      );
    }

    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(docUrl);
    const isPDF = /\.pdf$/i.test(docUrl);

    return (
      <div className="d-flex align-items-center gap-2 mb-2">
        <Badge bg={isImage ? "info" : isPDF ? "danger" : "secondary"}>
          {isImage ? (
            <FileEarmarkImage />
          ) : isPDF ? (
            <FileEarmarkPdf />
          ) : (
            <FileText />
          )}
        </Badge>
        <span className="small">{docName}</span>
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-2"
          onClick={() => handleViewImage(docUrl)}
        >
          <ZoomIn size={12} />
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => handleViewInNewTab(docUrl, docName)}
        >
          <Download size={12} />
        </Button>
      </div>
    );
  };

  // Filter employees locally for search (fallback)
  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.applicationID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.mobileNo?.includes(searchTerm),
  );

  // Pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => fetchEmployees(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    return items;
  };

  return (
    <Container fluid className="py-4">
      {/* Alert */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() =>
            setAlert({ show: false, message: "", variant: "success" })
          }
          dismissible
        >
          {alert.message}
        </Alert>
      )}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className=" text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-black">
                <PersonPlus className="me-2" />
                Field Officer Management
              </h5>
              <div className="d-flex gap-2">
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => fetchEmployees(currentPage)}
                  title="Refresh"
                >
                  <ArrowClockwise />
                </Button>
                <Button variant="light" size="sm" onClick={handleAddEmployee}>
                  <PersonPlus className="me-1" /> Add Field Officer
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              {/* Filters and Search */}
              <Row className="mb-4">
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search />
                    </InputGroup.Text>
                    <FormControl
                      placeholder="Search by name, ID, email, or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Filter />
                    </InputGroup.Text>
                    <Form.Select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="agent">Agent</option>
                      <option value="inspector">Inspector</option>
                      <option value="yardOwner">YardOwner</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Filter />
                    </InputGroup.Text>
                    <Form.Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="Approved">Approved</option>
                      <option value="Pending">Pending</option>
                      <option value="Rework">Rework</option>
                      <option value="Rejected">Rejected</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
              </Row>

              {/* Summary Stats */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="border-primary">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Total Officers</h6>
                          <h4 className="mb-0 text-primary">
                            {employees?.length}
                          </h4>
                        </div>
                        <div onClick={() => navigate("/field-officers/add")}>
                          <PersonPlus size={32} className="text-primary" />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-success">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Approved</h6>
                          <h4 className="mb-0 text-success">
                            {
                              employees.filter(
                                (e) => e.empStatus === "Approved",
                              ).length
                            }
                          </h4>
                        </div>
                        <ShieldCheck size={32} className="text-success" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-warning">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Pending</h6>
                          <h4 className="mb-0 text-warning">
                            {
                              employees.filter((e) => e.empStatus === "Pending")
                                .length
                            }
                          </h4>
                        </div>
                        <FileText size={32} className="text-warning" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-info">
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">Rework</h6>
                          <h4 className="mb-0 text-info">
                            {
                              employees.filter((e) => e.empStatus === "Rework")
                                .length
                            }
                          </h4>
                        </div>
                        <ArrowClockwise size={32} className="text-info" />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Employees Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading field officers...</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table striped bordered hover className="mb-0">
                      <thead>
                        <tr className="bg-light">
                          <th>#</th>
                          <th>Application ID</th>
                          <th>Name</th>
                          <th>Contact</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Documents</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployees.length > 0 ? (
                          filteredEmployees.map((employee, index) => (
                            <tr key={employee._id}>
                              <td className="px-2">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td className="px-2">
                                <strong>{employee.applicationID}</strong>
                                <div className="small text-muted">
                                  ID: {employee._id?.substring(0, 8)}...
                                </div>
                              </td>
                              <td className="px-2">
                                <strong>{employee.name}</strong>
                                <div className="small text-muted">
                                  Father: {employee.fatherName}
                                </div>
                              </td>
                              <td className="px-2">
                                <div className="d-flex flex-column mx-2">
                                  <span>
                                    <Envelope
                                      className="me-1 text-muted"
                                      size={14}
                                    />
                                    {employee.email}
                                  </span>
                                  <span>
                                    <Telephone
                                      className="me-1 text-muted"
                                      size={14}
                                    />
                                    {employee.mobileNo}
                                  </span>
                                </div>
                              </td>
                              <td className="px-2">
                                <Badge bg={getRoleBadgeColor(employee.role)}>
                                  {employee.role?.toUpperCase()}
                                </Badge>
                              </td>
                              <td className="px-2">
                                <Badge
                                  bg={getStatusBadgeColor(employee.empStatus)}
                                >
                                  {employee.empStatus}
                                </Badge>
                              </td>
                              <td className="px-2">
                                <div className="small">
                                  {employee.addressProof ? (
                                    <Badge bg="success" className="me-1">
                                      <House size={10} /> Addr
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary" className="me-1">
                                      <House size={10} /> Addr
                                    </Badge>
                                  )}
                                  {employee.panCard ? (
                                    <Badge bg="success" className="me-1">
                                      <PersonBadge size={10} /> PAN
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary" className="me-1">
                                      <PersonBadge size={10} /> PAN
                                    </Badge>
                                  )}
                                  {employee.supportingDocument ? (
                                    <Badge bg="success" className="me-1">
                                      <FileText size={10} /> Doc
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary" className="me-1">
                                      <FileText size={10} /> Doc
                                    </Badge>
                                  )}
                                  {employee.bankDetails?.cancelledCheque ? (
                                    <Badge bg="success" className="me-1">
                                      <Bank size={10} /> Bank
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary" className="me-1">
                                      <Bank size={10} /> Bank
                                    </Badge>
                                  )}
                                  {employee.compliance?.DRACertificate ? (
                                    <Badge bg="success" className="me-1">
                                      <Shield size={10} /> DRA
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary" className="me-1">
                                      <Shield size={10} /> DRA
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              <td className="px-2">
                                {new Date(
                                  employee.createdAt,
                                ).toLocaleDateString()}
                                <div className="small text-muted">
                                  {new Date(
                                    employee.createdAt,
                                  ).toLocaleTimeString()}
                                </div>
                              </td>
                              <td>
                                <Dropdown>
                                  <Dropdown.Toggle
                                    variant="light"
                                    size="sm"
                                    id="dropdown-basic"
                                  >
                                    <ThreeDotsVertical size={16} />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      onClick={() =>
                                        openModal("view", employee)
                                      }
                                    >
                                      <Eye className="me-2" size={14} />
                                      View Details
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleEdit(employee)}
                                    >
                                      <Pencil className="me-2" size={14} />
                                      Edit Officer
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      onClick={() =>
                                        handleUpdateStatus(employee, "Approved")
                                      }
                                      className="text-success"
                                    >
                                      <ShieldCheck className="me-2" size={14} />
                                      Approve
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() =>
                                        handleUpdateStatus(employee, "Rework")
                                      }
                                      className="text-info"
                                    >
                                      <ArrowClockwise
                                        className="me-2"
                                        size={14}
                                      />
                                      Mark as Rework
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() =>
                                        handleUpdateStatus(employee, "Rejected")
                                      }
                                      className="text-danger"
                                    >
                                      <XCircle className="me-2" size={14} />
                                      Reject
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      onClick={() =>
                                        openModal("delete", employee)
                                      }
                                      className="text-danger"
                                    >
                                      <Trash className="me-2" size={14} />
                                      Delete
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-5">
                              <div className="text-muted">
                                <PersonX size={48} className="mb-3" />
                                <h5>No field officers found</h5>
                                <p>Try adjusting your search or filters</p>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleAddEmployee}
                                >
                                  <PersonPlus className="me-1" /> Add First
                                  Officer
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, totalEmployees)}{" "}
                        of {totalEmployees} officers
                      </div>
                      <Pagination>
                        <Pagination.First
                          onClick={() => fetchEmployees(1)}
                          disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                          onClick={() => fetchEmployees(currentPage - 1)}
                          disabled={currentPage === 1}
                        />
                        {renderPaginationItems()}
                        <Pagination.Next
                          onClick={() => fetchEmployees(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                          onClick={() => fetchEmployees(totalPages)}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Modals */}
      {/* Enhanced View Employee Modal with Documents */}
      <Modal
        show={showModal && modalType === "view"}
        onHide={closeModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Eye className="me-2" />
            Field Officer Details - {currentEmployee?.applicationID}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEmployee && (
            <Tab.Container defaultActiveKey="personal">
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="personal">
                    <PersonCheck className="me-1" /> Personal Info
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="documents">
                    <FileText className="me-1" /> Documents
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="bank">
                    <Bank className="me-1" /> Bank Details
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="compliance">
                    <Shield className="me-1" /> Compliance
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="actions">
                    <ClipboardCheck className="me-1" /> Actions
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                {/* Personal Information Tab */}
                <Tab.Pane eventKey="personal">
                  <Row>
                    <Col md={6}>
                      <h6>Basic Information</h6>
                      <ListGroup variant="flush">
                        <ListGroup.Item>
                          <strong>Application ID:</strong>{" "}
                          {currentEmployee.applicationID}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>Name:</strong> {currentEmployee.name}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>Father's Name:</strong>{" "}
                          {currentEmployee.fatherName}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>Date of Birth:</strong>{" "}
                          {new Date(currentEmployee.dOB).toLocaleDateString()}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>Email:</strong> {currentEmployee.email}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>Phone:</strong> {currentEmployee.mobileNo}
                        </ListGroup.Item>
                      </ListGroup>
                    </Col>
                    <Col md={6}>
                      <h6>Address & Organization</h6>
                      <ListGroup variant="flush">
                        <ListGroup.Item>
                          <strong>Current Address:</strong>{" "}
                          {currentEmployee.currentAddress}
                        </ListGroup.Item>
                        {currentEmployee.organisation && (
                          <>
                            <ListGroup.Item>
                              <strong>Organization:</strong>{" "}
                              {currentEmployee.organisation.name}
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <strong>Org Phone:</strong>{" "}
                              {currentEmployee.organisation.phn}
                            </ListGroup.Item>
                            <ListGroup.Item>
                              <strong>Org Email:</strong>{" "}
                              {currentEmployee.organisation.email}
                            </ListGroup.Item>
                          </>
                        )}
                        <ListGroup.Item>
                          <strong>Role:</strong>{" "}
                          <Badge bg={getRoleBadgeColor(currentEmployee.role)}>
                            {currentEmployee.role?.toUpperCase()}
                          </Badge>
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>Status:</strong>{" "}
                          <Badge
                            bg={getStatusBadgeColor(currentEmployee.empStatus)}
                          >
                            {currentEmployee.empStatus}
                          </Badge>
                        </ListGroup.Item>
                      </ListGroup>
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* Documents Tab */}
                <Tab.Pane eventKey="documents">
                  <h6>Uploaded Documents</h6>
                  <Row>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header>
                          <House className="me-2" />
                          Address Proof
                        </Card.Header>
                        <Card.Body>
                          {renderDocumentPreview(
                            currentEmployee.addressProof,
                            "Address Proof",
                          )}
                          <p className="text-muted small mb-0">
                            Proof of current residential address
                          </p>
                        </Card.Body>
                      </Card>

                      <Card className="mb-3">
                        <Card.Header>
                          <PersonBadge className="me-2" />
                          PAN Card
                        </Card.Header>
                        <Card.Body>
                          <p className="text-muted small mb-0">
                            Permanent Account Number card : {currentEmployee.panCard}
                          </p>
                        </Card.Body>
                      </Card>

                      <Card>
                        <Card.Header>
                          <FileText className="me-2" />
                          Supporting Document
                        </Card.Header>
                        <Card.Body>
                          {renderDocumentPreview(
                            currentEmployee.supportingDocument,
                            "Supporting Document",
                          )}
                          <p className="text-muted small mb-0">
                            Additional supporting document
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* Bank Details Tab */}
                <Tab.Pane eventKey="bank">
                  <h6>Bank Account Information</h6>
                  <Row>
                    <Col md={6}>
                      <ListGroup variant="flush">
                        <ListGroup.Item>
                          <strong>Bank Name:</strong>{" "}
                          {currentEmployee.bankDetails?.bankName || "N/A"}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>Account Number:</strong>{" "}
                          {currentEmployee.bankDetails?.bankAccountNo || "N/A"}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          <strong>IFSC Code:</strong>{" "}
                          {currentEmployee.bankDetails?.ifscCode || "N/A"}
                        </ListGroup.Item>
                      </ListGroup>
                    </Col>
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <CreditCard className="me-2" />
                          Cancelled Cheque
                        </Card.Header>
                        <Card.Body>
                          {renderDocumentPreview(
                            currentEmployee.bankDetails?.cancelledCheque,
                            "Cancelled Cheque",
                          )}
                          <p className="text-muted small mb-0">
                            Cancelled cheque leaf for bank verification
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* Compliance Tab */}
                <Tab.Pane eventKey="compliance">
                  <h6>Compliance Documents</h6>
                  <Row>
                    <Col md={6}>
                      <Card className="mb-3">
                        <Card.Header>
                          <Shield className="me-2" />
                          DRA Certificate
                        </Card.Header>
                        <Card.Body>
                          {renderDocumentPreview(
                            currentEmployee.compliance?.DRACertificate,
                            "DRA Certificate",
                          )}
                          <p className="text-muted small mb-0">
                            DRA (Drug Regulatory Authority) Certificate
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card>
                        <Card.Header>
                          <ShieldCheck className="me-2" />
                          Police Verification Report
                        </Card.Header>
                        <Card.Body>
                          {renderDocumentPreview(
                            currentEmployee.compliance
                              ?.policeVerificationReport,
                            "Police Verification Report",
                          )}
                          <p className="text-muted small mb-0">
                            Police verification report for background check
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* Actions Tab */}
                <Tab.Pane eventKey="actions">
                  <h6>Document Verification & Approval</h6>
                  <Alert variant="info">
                    <strong>Verification Status:</strong> Review all documents
                    before approving the officer.
                  </Alert>

                  <div className="mb-4">
                    <h6>Document Checklist:</h6>
                    <Form>
                      <Form.Check
                        type="checkbox"
                        id="addressProof"
                        label="Address Proof Verified"
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        id="panCard"
                        label="PAN Card Verified"
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        id="supportingDoc"
                        label="Supporting Document Verified"
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        id="bankCheque"
                        label="Cancelled Cheque Verified"
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        id="draCertificate"
                        label="DRA Certificate Verified"
                        className="mb-2"
                      />
                      <Form.Check
                        type="checkbox"
                        id="policeVerification"
                        label="Police Verification Verified"
                        className="mb-2"
                      />
                    </Form>
                  </div>

                  <div className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        <strong>Approval Remarks:</strong>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter remarks for approval, rework, or rejection..."
                        id="approvalRemarks"
                      />
                    </Form.Group>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      size="lg"
                      onClick={() => {
                        const remarks =
                          document.getElementById("approvalRemarks").value;
                        handleUpdateStatus(
                          currentEmployee,
                          "Approved",
                          remarks,
                        );
                      }}
                    >
                      <CheckCircle className="me-2" />
                      Approve Officer
                    </Button>
                    <Button
                      variant="warning"
                      size="lg"
                      onClick={() => {
                        const remarks =
                          document.getElementById("approvalRemarks").value;
                        handleUpdateStatus(currentEmployee, "Rework", remarks);
                      }}
                    >
                      <ArrowClockwise className="me-2" />
                      Request Rework
                    </Button>
                    <Button
                      variant="danger"
                      size="lg"
                      onClick={() => {
                        const remarks =
                          document.getElementById("approvalRemarks").value;
                        handleUpdateStatus(
                          currentEmployee,
                          "Rejected",
                          remarks,
                        );
                      }}
                    >
                      <XCircle className="me-2" />
                      Reject Application
                    </Button>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
          <Button variant="primary" onClick={() => handleEdit(currentEmployee)}>
            <Pencil className="me-1" /> Edit Officer
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Image Preview Modal */}
      <Modal
        show={imageModalShow}
        onHide={() => setImageModalShow(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Document Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <Image
              src={selectedImage}
              alt="Document Preview"
              fluid
              className="rounded"
              style={{ maxHeight: "70vh" }}
            />
          ) : (
            <p className="text-muted">No image to display</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setImageModalShow(false)}>
            Close
          </Button>
          {selectedImage && (
            <Button
              variant="primary"
              onClick={() => handleDownload(selectedImage, "document")}
            >
              <Download className="me-1" /> Download
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal show={showModal && modalType === "delete"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <Trash className="me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>Warning:</strong> This action cannot be undone.
          </Alert>
          <p>
            Are you sure you want to delete field officer{" "}
            <strong>{currentEmployee?.name}</strong> (ID:{" "}
            {currentEmployee?.applicationID})?
          </p>
          <p className="text-muted small">
            This will permanently remove all associated data including
            documents.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete Officer"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Send OTP Modal */}
      <Modal show={showModal && modalType === "sendOtp"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <Send className="me-2" />
            Send OTP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Send OTP to <strong>{currentEmployee?.name}</strong>?
          </p>
          <div className="alert alert-info">
            <p className="mb-1">
              <strong>Phone:</strong> {currentEmployee?.mobileNo}
            </p>
            <p className="mb-0">
              <strong>Email:</strong> {currentEmployee?.email}
            </p>
          </div>
          <p className="text-muted small">
            OTP will be sent to the registered phone number and email.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendOtp}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              "Send OTP"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Reset Password Modal */}
      <Modal
        show={showModal && modalType === "resetPassword"}
        onHide={closeModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Key className="me-2" />
            Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Send password reset instructions to{" "}
            <strong>{currentEmployee?.name}</strong>?
          </p>
          <div className="alert alert-warning">
            <p className="mb-0">
              A password reset OTP will be sent to:{" "}
              <strong>{currentEmployee?.email}</strong>
            </p>
          </div>
          <p className="text-muted small">
            The officer will need to use the OTP to set a new password.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleResetPassword}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              "Send Reset Instructions"
            )}
          </Button>
        </Modal.Footer>
      </Modal>{" "}
    </Container>
  );
};

export default EmployeeList;
